import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { apiFetch } from '../../../shared/lib/apiClient';

const PERMISSION_LABELS = {
  view_invoices:   'View Invoices',
  upload_invoices: 'Upload Invoices',
  view_analytics:  'View Analytics',
  manage_team:     'Manage Team',
  manage_settings: 'Manage Settings',
};

const DEFAULT_PERMISSIONS = {
  view_invoices:   true,
  upload_invoices: false,
  view_analytics:  false,
  manage_team:     false,
  manage_settings: false,
};

/* ── Role Edit Modal ── */
const RoleEditModal = ({ role, onClose, onSave }) => {
  const isNew = !role;
  const [name,        setName]        = useState(role?.name        || '');
  const [description, setDescription] = useState(role?.description || '');
  const [permissions, setPermissions] = useState(role?.permissions || { ...DEFAULT_PERMISSIONS });
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState('');
  const overlayRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const togglePerm = (key) => {
    setPermissions(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = async () => {
    if (!name.trim()) { setError('Role name is required'); return; }
    setSaving(true);
    setError('');
    try {
      const body = { name: name.trim(), description: description.trim() || null, permissions };
      const res = isNew
        ? await apiFetch('/api/roles', { method: 'POST', body: JSON.stringify(body) })
        : await apiFetch(`/api/roles/${role.id}`, { method: 'PUT', body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed to save'); return; }
      onSave(data.role);
      onClose();
    } catch {
      setError('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.3)' }}
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl shadow-zinc-900/20 w-full max-w-sm mx-4 overflow-hidden">
        {/* Header */}
        <div className="px-6 py-5 border-b border-zinc-100 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-medium mb-0.5">{isNew ? 'New Role' : 'Edit Role'}</p>
            <p className="text-sm font-medium text-zinc-900">{isNew ? 'Create a custom role' : name}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-zinc-100 transition-colors text-zinc-400 hover:text-zinc-700">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <path d="M1 1l12 12M13 1L1 13" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          {error && (
            <div className="px-4 py-2.5 bg-red-50 text-red-500 text-xs rounded-lg border border-red-100">{error}</div>
          )}

          <div>
            <label className="block text-[10px] uppercase tracking-widest text-zinc-400 font-medium mb-1.5">Role Name</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Accountant, Viewer"
              maxLength={50}
              className="w-full px-3 py-2 text-sm bg-zinc-50 border border-zinc-200 rounded-lg outline-none focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900 transition-shadow placeholder-zinc-300"
            />
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-widest text-zinc-400 font-medium mb-1.5">Description <span className="normal-case tracking-normal text-zinc-300">(optional)</span></label>
            <input
              type="text"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Brief description of this role"
              maxLength={200}
              className="w-full px-3 py-2 text-sm bg-zinc-50 border border-zinc-200 rounded-lg outline-none focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900 transition-shadow placeholder-zinc-300"
            />
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-widest text-zinc-400 font-medium mb-2">Permissions</label>
            <div className="space-y-2">
              {Object.entries(PERMISSION_LABELS).map(([key, label]) => (
                <label key={key} className="flex items-center gap-3 cursor-pointer group">
                  <div
                    onClick={() => togglePerm(key)}
                    className={`w-8 h-4.5 rounded-full relative transition-colors cursor-pointer flex-shrink-0 ${
                      permissions[key] ? 'bg-zinc-900' : 'bg-zinc-200'
                    }`}
                    style={{ width: 32, height: 18 }}
                  >
                    <div className={`absolute top-0.5 w-3.5 h-3.5 rounded-full bg-white shadow transition-transform ${
                      permissions[key] ? 'translate-x-3.5' : 'translate-x-0.5'
                    }`} style={{ width: 14, height: 14, top: 2 }} />
                  </div>
                  <span className="text-sm text-zinc-700">{label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-zinc-100 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-xs text-zinc-500 hover:text-zinc-800 transition-colors">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 bg-zinc-900 text-white text-xs rounded-lg hover:bg-zinc-700 transition-colors disabled:opacity-50"
          >
            {saving && <span className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" />}
            {isNew ? 'Create role' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

/* ── Permission badge ── */
const PermBadge = ({ label, active }) => (
  <span className={`text-[10px] px-2 py-0.5 rounded-md font-medium ${
    active ? 'bg-emerald-50 text-emerald-700' : 'bg-zinc-100 text-zinc-300 line-through'
  }`}>
    {label}
  </span>
);

/* ── Main RolesTab ── */
const RolesTab = ({ roles, setRoles, canManage }) => {
  const [editingRole,  setEditingRole]  = useState(null);  // null = creating new
  const [showModal,    setShowModal]    = useState(false);
  const [deletingId,   setDeletingId]   = useState(null);
  const [deleteError,  setDeleteError]  = useState('');

  const handleSaved = (savedRole) => {
    setRoles(prev => {
      const existing = prev.find(r => r.id === savedRole.id);
      if (existing) return prev.map(r => r.id === savedRole.id ? { ...r, ...savedRole } : r);
      return [...prev, { ...savedRole, memberCount: 0 }];
    });
  };

  const handleDelete = async (roleId) => {
    setDeletingId(roleId);
    setDeleteError('');
    try {
      const res = await apiFetch(`/api/roles/${roleId}`, { method: 'DELETE' });
      if (!res.ok) {
        const data = await res.json();
        setDeleteError(data.error || 'Failed to delete');
        return;
      }
      setRoles(prev => prev.filter(r => r.id !== roleId));
    } catch {
      setDeleteError('Failed to delete');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-xl shadow-sm">
      {/* Header */}
      <div className="px-5 py-4 border-b border-zinc-100 flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-medium">Custom Roles</p>
        {canManage && (
          <button
            onClick={() => { setEditingRole(null); setShowModal(true); }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-zinc-900 text-white text-[10px] uppercase tracking-widest rounded-lg hover:bg-zinc-700 transition-colors"
          >
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
              <path d="M5 1v8M1 5h8" />
            </svg>
            New Role
          </button>
        )}
      </div>

      {deleteError && (
        <div className="px-5 py-2.5 bg-red-50 text-red-500 text-xs border-b border-red-100">{deleteError}</div>
      )}

      {roles.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-12 h-12 rounded-2xl bg-zinc-50 flex items-center justify-center mb-3">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" className="text-zinc-200">
              <path d="M12 2a5 5 0 1 0 0 10A5 5 0 0 0 12 2z" />
              <path d="M19 21a7 7 0 1 0-14 0" />
            </svg>
          </div>
          <p className="text-sm text-zinc-400">No custom roles yet</p>
          {canManage && (
            <button
              onClick={() => { setEditingRole(null); setShowModal(true); }}
              className="mt-3 text-xs text-zinc-500 underline underline-offset-2 hover:text-zinc-800 transition-colors"
            >
              Create your first role
            </button>
          )}
        </div>
      ) : (
        <div className="divide-y divide-zinc-50">
          {roles.map(role => (
            <div key={role.id} className="px-5 py-4 group">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-sm font-medium text-zinc-900">{role.name}</p>
                    <span className="text-[10px] text-zinc-400 tabular-nums">
                      {role.memberCount} {role.memberCount === 1 ? 'member' : 'members'}
                    </span>
                  </div>
                  {role.description && (
                    <p className="text-[11px] text-zinc-400 mb-2">{role.description}</p>
                  )}
                  <div className="flex flex-wrap gap-1.5">
                    {Object.entries(PERMISSION_LABELS).map(([key, label]) => (
                      <PermBadge key={key} label={label} active={!!role.permissions?.[key]} />
                    ))}
                  </div>
                </div>
                {canManage && (
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                    <button
                      onClick={() => { setEditingRole(role); setShowModal(true); }}
                      className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700 transition-colors"
                      title="Edit role"
                    >
                      <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11.5 2.5a2.121 2.121 0 0 1 3 3L5 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDelete(role.id)}
                      disabled={deletingId === role.id}
                      className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 text-zinc-300 hover:text-red-500 transition-colors disabled:opacity-50"
                      title="Delete role"
                    >
                      {deletingId === role.id
                        ? <span className="w-3 h-3 border border-zinc-400 border-t-transparent rounded-full animate-spin" />
                        : <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M2 4h12M5 4V2h6v2M6 7v5M10 7v5M3 4l1 10h8l1-10" />
                          </svg>
                      }
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <RoleEditModal
          role={editingRole}
          onClose={() => setShowModal(false)}
          onSave={handleSaved}
        />
      )}
    </div>
  );
};

export default RolesTab;
